import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import type { AnchorMap, GType } from './labelSprings';
import type { GapTimes, Reading, Scales } from './scales';

export type TimeGType = Exclude<GType, 'gapDistance' | 'gapDistanceEx'>;
export type DistanceGType = Extract<GType, 'gapDistance' | 'gapDistanceEx'>;

/** A time gap: horizontal from x1 to x2 at offset y1. */
export type TimeGapSegment = { x1: number; x2: number; y1: number; value: number };
/** A distance gap: vertical from y1 to y2 at time x1. */
export type DistanceGapSegment = { x1: number; y1: number; y2: number; value: number };

export type GapSegments = Partial<Record<TimeGType, TimeGapSegment>> &
  Partial<Record<DistanceGType, DistanceGapSegment>>;

/**
 * Where each gap sits in the plot. The measured gaps hang off gapCalcTime, the extrapolated
 * (Ex) ones off resultTime.
 */
export function gapSegments(
  s: Reading,
  leader: Reading | null,
  { resultTime, gapCalcTime, leaderDefinesGapCalcTime }: GapTimes,
  { toX, toY }: Scales,
): GapSegments {
  const segments: GapSegments = {};
  const leaderFromStart = leaderDefinesGapCalcTime ? (leader?.fromStart ?? null) : null;
  // Gaps behind the starter reach back in time from `origin`, chase gaps forward.
  const timeGapFrom =
    (origin: number) =>
    (start: number, value: number, direction: -1 | 1): TimeGapSegment => ({
      x1: toX(origin),
      x2: toX(origin + direction * OneSecondInMillis * value),
      y1: toY(start),
      value,
    });

  if (gapCalcTime != null && s.fromStart != null) {
    const x1 = toX(gapCalcTime);
    // The starter's offset as the gap calculation saw it.
    const behind =
      leaderFromStart != null && s.gapDistance != null
        ? leaderFromStart - s.gapDistance
        : s.fromStart;
    const timeGap = timeGapFrom(gapCalcTime);

    if (s.gapDistance != null) {
      const start = leaderFromStart ?? s.fromStart;
      segments.gapDistance = {
        x1,
        y1: toY(start),
        y2: toY(leaderDefinesGapCalcTime ? start - s.gapDistance : start + s.gapDistance),
        value: s.gapDistance,
      };
    }
    if (s.gap != null) segments.gap = timeGap(behind, s.gap, -1);
    if (s.gapChase != null && s.gapDistance != null) {
      const ahead = leaderFromStart ?? s.fromStart + s.gapDistance;
      segments.gapChase = timeGap(ahead, s.gapChase, 1);
    }
    if (s.gapAverage != null) segments.gapAverage = timeGap(behind, s.gapAverage, -1);
  }

  if (resultTime != null) {
    const x1 = toX(resultTime);
    const timeGap = timeGapFrom(resultTime);

    if (s.fromStartEx != null) {
      if (s.gapEx != null) segments.gapEx = timeGap(s.fromStartEx, s.gapEx, -1);
      if (s.gapDistanceEx != null) {
        segments.gapDistanceEx = {
          x1,
          y1: toY(s.fromStartEx),
          y2: toY(s.fromStartEx + s.gapDistanceEx),
          value: s.gapDistanceEx,
        };
      }
      if (s.gapAverageEx != null)
        segments.gapAverageEx = timeGap(s.fromStartEx, s.gapAverageEx, -1);
    }
    if (leader?.fromStartEx != null && s.gapChaseEx != null) {
      segments.gapChaseEx = timeGap(leader.fromStartEx, s.gapChaseEx, 1);
    }
  }

  return segments;
}

/**
 * The resting place of each gap label, or null while any gap is missing — the springs need
 * all eight.
 */
export function gapAnchors(
  segments: GapSegments,
  s: Reading,
  leader: Reading | null,
  { gapCalcTime }: GapTimes,
  { toX }: Scales,
): AnchorMap | null {
  const { gapDistance, gap, gapChase, gapAverage, gapEx, gapDistanceEx, gapChaseEx, gapAverageEx } =
    segments;
  if (
    gapDistance == null ||
    gap == null ||
    gapChase == null ||
    gapAverage == null ||
    gapEx == null ||
    gapDistanceEx == null ||
    gapChaseEx == null ||
    gapAverageEx == null ||
    s.fromStart == null ||
    leader?.fromStart == null
  ) {
    return null;
  }
  const above = (g: TimeGapSegment, dy: number): [number, number] => [
    0.5 * (g.x1 + g.x2),
    g.y1 - dy,
  ];
  const beside = (g: DistanceGapSegment, dx: number): [number, number] => [
    g.x1 + dx,
    0.5 * (g.y1 + g.y2),
  ];
  return {
    gapDistance: beside(gapDistance, 0.08),
    gapDistanceEx: beside(
      gapDistanceEx,
      gapCalcTime != null ? 0.16 + toX(gapCalcTime) - gapDistanceEx.x1 : 0.16,
    ),
    gap: above(gap, 0.08),
    gapEx: above(gapEx, 0.16),
    gapChase: above(gapChase, -0.08),
    gapChaseEx: above(gapChaseEx, -0.16),
    gapAverage: above(gapAverage, -0.08),
    gapAverageEx: above(gapAverageEx, -0.16),
  };
}
